import { motion } from "framer-motion";
import { MapPin, Ruler, ArrowUpRight, Sparkles } from "lucide-react";
import TiltCard from "./TiltCard";
import { formatMXN, resolveImage } from "../lib/api";

export default function PropertyGrid({ properties, onSelect, loading, content = {} }) {
  // Show only non-presale lots here (presale has its own section)
  const lots = properties.filter((p) => !p.is_presale);

  return (
    <section
      id="lotes"
      data-testid="properties-section"
      className="relative py-28 bg-gradient-to-b from-[#0B1A0F] via-[#15301E] to-[#0B1A0F] overflow-hidden"
    >
      <div className="orb w-[500px] h-[500px] bg-[#B8E07A]/25 -bottom-32 -left-40" />
      <div className="orb w-[400px] h-[400px] bg-[#F1C36B]/20 top-20 -right-32" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl text-center mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-medium text-[#F1C36B]">
            <Sparkles className="w-4 h-4" /> {content.lotes_overline || "Los 12 lotes ecocampestres"}
          </span>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F2EFE6] leading-[1.05]">
            {content.lotes_title_1 || "Cada lote es"}{" "}
            <span className="font-fairy text-[#B8E07A]">{content.lotes_title_fairy || "un capítulo"}</span>{" "}
            {content.lotes_title_2 || "distinto"}
          </h2>
          <p className="mt-5 text-[#F2EFE6]/70 text-base lg:text-lg leading-relaxed">
            {content.lotes_description || "3,000 m² cada uno · barreras orgánicas, no vallas · acceso a todas las amenidades comunes y al arroyo natural."}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-9" style={{ perspective: "1400px" }}>
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-[460px] rounded-3xl bg-[#15301E]/40 animate-pulse"
              />
            ))}
          {!loading &&
            lots.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 60, rotateX: 12 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.85,
                  delay: (i % 3) * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{ transformStyle: "preserve-3d" }}
              >
                <TiltCard>
                  <button
                    onClick={() => onSelect(p)}
                    data-testid={`property-card-${p.lot_number}`}
                    className="group w-full text-left rounded-3xl overflow-hidden bg-gradient-to-br from-[#15301E] to-[#0B1A0F] border border-[#B8E07A]/15 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.7)] hover:shadow-[0_45px_100px_-25px_rgba(184,224,122,0.25)] transition-shadow duration-500"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={resolveImage(p.image)}
                        alt={p.name}
                        className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A0F] via-[#0B1A0F]/30 to-transparent" />
                      <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#0B1A0F]/70 backdrop-blur px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.15em] font-medium text-[#F1C36B] border border-[#F1C36B]/30">
                        Lote #{p.lot_number}
                      </div>
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-[#0B1A0F]/70 backdrop-blur px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest text-[#B8E07A] border border-[#B8E07A]/25">
                        <MapPin className="w-3 h-3" /> Pextlán · Xico
                      </div>
                      {!p.available && (
                        <div className="absolute bottom-4 right-4 bg-[#0B1A0F]/80 text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">
                          Reservado
                        </div>
                      )}
                    </div>
                    <div className="p-6 tilt-inner">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="font-serif text-2xl text-[#F2EFE6] leading-tight group-hover:text-[#F1C36B] transition-colors">
                          {p.name}
                        </h3>
                        <span className="mt-1 w-10 h-10 rounded-full bg-[#0B1A0F] border border-[#B8E07A]/30 flex items-center justify-center text-[#B8E07A] transition-all group-hover:bg-[#B8E07A] group-hover:text-[#0B1A0F] group-hover:rotate-45">
                          <ArrowUpRight className="w-4 h-4" />
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-[#F2EFE6]/65 line-clamp-2">
                        {p.description}
                      </p>
                      <div className="mt-5 flex items-center justify-between pt-5 border-t border-dashed border-[#B8E07A]/15">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#F2EFE6]/50">
                            Desde
                          </div>
                          <div className="font-serif text-2xl text-[#F1C36B]">
                            {formatMXN(p.price)}
                          </div>
                          <div className="text-[10px] text-[#F2EFE6]/50 mt-0.5">
                            ${p.price_per_m2?.toLocaleString()} / m²
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-widest text-[#F2EFE6]/50">
                            Superficie
                          </div>
                          <div className="inline-flex items-center gap-1 font-medium text-[#B8E07A]">
                            <Ruler className="w-3.5 h-3.5" /> {p.surface.toLocaleString()} m²
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                </TiltCard>
              </motion.div>
            ))}
        </div>
      </div>
    </section>
  );
}
